import type { Build } from '@todesktop/shared';
import { Box, Text } from 'ink';
import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { where } from 'firebase/firestore';
import BuildTableLayout from './BuildTableLayout';
import useBuildTableState from './useBuildTableState';
import findAppUserId from '../utilities/findAppUserId';
import getBuilds from '../utilities/getBuilds';
import getLatestBuildId from '../utilities/getLatestBuildId';
import getLatestReleasedBuild from '../utilities/getLatestReleasedBuild';
import getRelativeDateFromDateString from '../utilities/getRelativeDateFromDateString';
import getProjectConfig from '../utilities/projectConfig/getProjectConfig';
import validateBuildIdArg from '../utilities/validateBuildIdArg';

export default function BuildPicker({
  buildFilter,
  buildId,
  children,
  commandUsed,
  configPath,
  loadData = loadPickerData,
  noBuildsDescription = null,
  question = 'Which build would you like to test?',
}: {
  buildFilter?: BuildFilter;
  buildId?: string;
  children: (buildId: string) => JSX.Element;
  commandUsed: string;
  configPath?: string;
  loadData?: typeof loadPickerData;
  noBuildsDescription?: string | null | JSX.Element;
  question?: string | JSX.Element;
}) {
  const [selectedBuildId, setSelectedBuildId] = useState<string | undefined>(
    buildId && buildId !== 'latest' ? buildId : undefined,
  );

  const load = useCallback(async () => {
    return loadData({
      buildFilter,
      buildId,
      commandUsed,
      configPath,
    });
  }, [buildFilter, buildId, commandUsed, configPath, loadData]);

  const { status, builds, error, selectedId, selectItem } =
    useBuildTableState<BuildDisplayItem>({
      load,
    });

  useEffect(() => {
    if (selectedId) {
      setSelectedBuildId(selectedId);
    }
  }, [selectedId]);

  const noBuildsContent = useMemo(
    () => (
      <Box>
        <Text>No eligible builds found </Text>
        {noBuildsDescription && <Text dimColor>{noBuildsDescription}</Text>}
      </Box>
    ),
    [noBuildsDescription],
  );

  const header = useMemo(
    () => (
      <Box>
        <Text>{question}</Text>
      </Box>
    ),
    [question],
  );

  const footer = useMemo(() => {
    if (status !== 'show-builds') {
      return null;
    }
    return (
      <Box>
        <Text dimColor>
          Showing the latest {builds.length} unreleased successful builds
        </Text>
      </Box>
    );
  }, [builds.length, status]);

  if (selectedBuildId) {
    return children(selectedBuildId);
  }

  return (
    <BuildTableLayout
      builds={builds}
      commandUsed={commandUsed}
      error={error}
      footer={footer}
      header={header}
      noBuildsContent={noBuildsContent}
      onSelect={({ value }) => {
        selectItem(value.ID);
        setSelectedBuildId(value.ID);
      }}
      status={status}
    />
  );
}

async function loadPickerData({
  buildFilter,
  buildId,
  commandUsed,
  configPath,
}: {
  buildFilter?: BuildFilter;
  buildId?: string;
  commandUsed: string;
  configPath?: string;
}): Promise<LoadPickerDataResult> {
  if (buildId && buildId !== 'latest') {
    validateBuildIdArg({ buildId, commandUsed });
    return { status: 'selected', selectedId: buildId };
  }

  if (buildId === 'latest') {
    const latestBuildId = await getLatestBuild({ configPath });
    if (latestBuildId) {
      return { status: 'selected', selectedId: latestBuildId };
    }
    return { status: 'no-builds' };
  }

  const builds = await getBuildItems({ buildFilter, configPath });
  if (builds.length === 0) {
    return { status: 'no-builds' };
  }
  return { status: 'builds', builds };
}

async function getLatestBuild({
  configPath,
}: {
  configPath?: string;
}): Promise<string | undefined> {
  const { id: appId } = getProjectConfig(configPath).config;
  const { id: userId } = await findAppUserId(appId);
  return getLatestBuildId({ appId, userId });
}

async function getBuildItems({
  buildFilter = () => true,
  configPath,
}: {
  buildFilter?: BuildFilter;
  configPath?: string;
}): Promise<BuildDisplayItem[]> {
  const { id: appId } = getProjectConfig(configPath).config;
  const { id: userId, label: userName } = await findAppUserId(appId);
  const latestReleasedBuild = await getLatestReleasedBuild({ appId, userId });
  const rawBuilds = await getBuilds({
    addWhereClauses: () => [where('status', '==', 'succeeded')],
    appId,
    limit: 50,
    userId,
  });

  return rawBuilds
    .filter((build) => buildFilter({ build, latestReleasedBuild }))
    .slice(0, 5)
    .map((build) => ({
      'ID': build.id,
      'Version': build.appVersion || 'unknown',
      'Creation date': getRelativeDateFromDateString(build.createdAt),
      'Owner': userName || 'unknown',
    }));
}

interface BuildDisplayItem {
  'Creation date': string;
  'ID': string;
  'Owner': string;
  'Version': string;
  [key: string]: string;
}

export type BuildFilter = (args: {
  build: Build;
  latestReleasedBuild?: Build;
}) => boolean;

type LoadPickerDataResult =
  | { status: 'builds'; builds: BuildDisplayItem[] }
  | { status: 'no-builds' }
  | { status: 'selected'; selectedId: string };
